import { useTranslation } from "react-i18next"

import { ClipboardButton } from "@/components/commons/clipboard-button"
import { cn } from "@/lib/utils"

interface InfoItemProps {
  label: string
  value?: string | null
  copyable?: boolean
  className?: string
  truncate?: boolean
  /**
   * Conteúdo no lugar do texto — badge, switch, link. Existe porque um rótulo com
   * controle ao lado é o mesmo par label+valor, e telas que precisavam disso vinham
   * recriando o componente localmente, cada uma com um espaçamento diferente.
   */
  children?: React.ReactNode
}

export const InfoItem = ({
  label,
  value,
  copyable,
  className,
  truncate,
  children,
}: InfoItemProps) => {
  const { t } = useTranslation()
  return (
    <div
      className={cn(
        "flex min-w-0 flex-col gap-0.5",
        className
      )}
    >
      <span className="text-xs font-medium text-muted-foreground">
        {label}
      </span>
      {children ? (
        <div className="flex min-w-0 items-center gap-1 text-sm">
          {children}
        </div>
      ) : (
        <div className="flex min-w-0 items-center gap-1">
          <span
            className={cn(
              "text-sm",
              truncate ? "truncate" : "break-all"
            )}
          >
            {value || t("common.notAvailable")}
          </span>
          {copyable && value && (
            <ClipboardButton text={value} />
          )}
        </div>
      )}
    </div>
  )
}

interface SectionCardProps {
  title: string
  icon?: React.ReactNode
  children: React.ReactNode
  className?: string
  action?: React.ReactNode
}

export const SectionCard = ({
  title,
  icon,
  children,
  className,
  action,
}: SectionCardProps) => (
  <div
    className={cn(
      "rounded-lg border border-border bg-card",
      className
    )}
  >
    <div className="flex items-center justify-between border-b border-border bg-muted/50 px-4 py-3">
      <div className="flex items-center gap-2">
        {icon && (
          <span className="text-muted-foreground">
            {icon}
          </span>
        )}
        <h3 className="text-sm font-semibold">{title}</h3>
      </div>
      {action}
    </div>
    <div className="p-4">{children}</div>
  </div>
)
